
import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Form } from "@/components/ui/form";
import { CompanyInfoFields } from "./CompanyInfoFields";
import { PersonalInfoFields } from "./PersonalInfoFields";
import { PasswordFields } from "./PasswordFields";
import { TermsCheckbox } from "./TermsCheckbox";
import { registerFormSchema, RegisterFormValues } from "@/types/auth";

export const RegisterForm = () => {
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();
  
  const form = useForm<RegisterFormValues>({
    resolver: zodResolver(registerFormSchema),
    defaultValues: {
      organization_name: "",
      first_name: "",
      last_name: "",
      email: "",
      mobile_number: "",
      city_id: 0,
      password1: "",
      password2: "",
      terms_and_conditions_ok: false,
    },
  });

  const onSubmit = async (data: RegisterFormValues) => {
    setIsLoading(true);
    
    try {
      // Prepare data, making sure to prepend +1 to the phone number
      const formData = {
        ...data,
        mobile_number: data.mobile_number.startsWith("+1") ? 
          data.mobile_number : 
          `+1${data.mobile_number}`
      };
      
      const response = await fetch("https://api.discoveryconciergeservices.com/api/v1/rest-auth/registration/", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });
      
      const contentType = response.headers.get("content-type");
      
      if (!response.ok) {
        if (contentType && contentType.includes("application/json")) {
          const result = await response.json();
          const errorMessage = Object.values(result).flat().join(", ");
          throw new Error(errorMessage || "Registration failed");
        } else {
          throw new Error(`Registration failed: ${response.status} ${response.statusText}`);
        }
      }
      
      let result;
      if (contentType && contentType.includes("application/json")) {
        result = await response.json();
        if (result.key) {
          localStorage.setItem("authToken", result.key);
        }
      }
      
      toast({
        title: "Account created",
        description: "Please verify your account with the code sent to your phone",
      });
      
      navigate("/verify", { state: { email: data.email } });
    } catch (error) {
      console.error("Registration error:", error);
      toast({
        title: "Registration failed",
        description: error instanceof Error ? error.message : "Something went wrong",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm w-full max-w-md p-8">
      <div className="flex justify-center mb-6">
        <div className="h-12 w-12 rounded-full bg-black flex items-center justify-center">
          <svg 
            xmlns="http://www.w3.org/2000/svg" 
            viewBox="0 0 24 24" 
            fill="none" 
            stroke="currentColor" 
            strokeWidth="2" 
            strokeLinecap="round" 
            strokeLinejoin="round" 
            className="h-6 w-6 text-green-400"
          >
            <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"></path>
            <circle cx="12" cy="7" r="4"></circle>
          </svg>
        </div>
      </div>
      
      <h1 className="text-center text-2xl font-medium text-black mb-8">
        Create your account
      </h1>
      
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">
          <CompanyInfoFields form={form} />
          <PersonalInfoFields form={form} />
          <PasswordFields form={form} />
          <TermsCheckbox form={form} />
          
          <Button 
            type="submit" 
            className="w-full bg-gradient-to-r from-green-400 to-teal-400 hover:opacity-90 mt-4 text-black"
            disabled={isLoading}
          >
            {isLoading ? "Registering..." : "Continue"} 
            {!isLoading && <ArrowRight className="ml-2" size={16} />}
          </Button>
        </form>
      </Form>
    </div>
  );
};
